export const MAX_BRAND_REGISTRY_REVIEWS = 10;

export type QueueViolation = {
  type: string;
  confidence: number;
  policyClause?: string;
  evidence?: string;
};

export type BatchableQueueReview = {
  id: string;
  asin: string;
  amazonReviewId: string;
  reviewUrl: string | null;
  rating: number;
  title: string | null;
  body: string;
  brand: { customerId: string } | null;
  customer: { id: string } | null;
  auditRequest: { email: string };
  classification: {
    violations: QueueViolation[];
    rationale: string | null;
    takedownRequest: string | null;
  };
};

export type TakedownQueueGroup<
  T extends BatchableQueueReview = BatchableQueueReview,
> = {
  key: string;
  ownerKey: string;
  category: string;
  batchNumber: number;
  batchCount: number;
  reviews: T[];
};

export type BrandRegistrySubmission = {
  asinField: string;
  reviewUrlsField: string;
  asins: string[];
  reviewUrls: string[];
};

export type BatchMessageProvenance =
  | { source: "llm"; modelId: string }
  | { source: "deterministic_fallback"; modelId: null };

const VIOLATION_LABELS: Record<string, string> = {
  seller_feedback_misplaced: "Off-topic seller feedback",
  conflict_of_interest: "Conflict of interest",
  incentivized: "Incentivized review",
  profanity_or_harassment: "Profanity / harassment",
  privacy_violation: "Privacy violation",
  off_topic: "Off-topic",
  duplicate_or_template: "Duplicate / template",
  manufacturer_misrepresentation: "Manufacturer misrepresentation",
  competitor_review: "Competitor review",
  review_swap: "Review swap",
  uncategorized: "Uncategorized",
};

const ACTIONABLE_VIOLATION_TYPES = new Set([
  "seller_feedback_misplaced",
  "conflict_of_interest",
  "incentivized",
  "profanity_or_harassment",
  "privacy_violation",
  "off_topic",
  "duplicate_or_template",
  "manufacturer_misrepresentation",
  "competitor_review",
  "review_swap",
]);

const VIOLATION_THRESHOLDS: Record<string, number> = {
  seller_feedback_misplaced: 0.65,
  conflict_of_interest: 0.8,
  incentivized: 0.75,
  profanity_or_harassment: 0.75,
  privacy_violation: 0.75,
  off_topic: 0.65,
  duplicate_or_template: 0.8,
  manufacturer_misrepresentation: 0.8,
  competitor_review: 0.8,
  review_swap: 0.75,
};

const TEXT_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export function violationLabel(type: string): string {
  return VIOLATION_LABELS[type] ?? type.replaceAll("_", " ");
}

function normalizeReviewPart(value: string | null): string {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

export function formatQueueReviewText(
  review: Pick<BatchableQueueReview, "title" | "body">
): string {
  const title = normalizeReviewPart(review.title);
  const body = normalizeReviewPart(review.body);
  if (!title) return body;
  if (!body || title.toLocaleLowerCase() === body.toLocaleLowerCase()) {
    return title;
  }
  return `${title}\n\n${body}`;
}

function matchesOrderedStrings(value: unknown, expected: string[]): boolean {
  return (
    Array.isArray(value) &&
    value.length === expected.length &&
    value.every(
      (item, index) =>
        typeof item === "string" && item === expected[index]
    )
  );
}

function matchesAsinSet(value: unknown, expected: string[]): boolean {
  if (!Array.isArray(value) || value.length !== expected.length) return false;
  const actual = value.map((item) =>
    typeof item === "string" ? item.trim().toUpperCase() : ""
  );
  const normalizedExpected = expected.map((asin) =>
    asin.trim().toUpperCase()
  );
  return (
    actual.every(Boolean) &&
    new Set(actual).size === actual.length &&
    normalizedExpected.every((asin) => actual.includes(asin))
  );
}

export function batchMessageMatchesSubmission(
  response: { category: unknown; asins: unknown; reviewUrls: unknown },
  category: string,
  submission: Pick<BrandRegistrySubmission, "asins" | "reviewUrls">
): boolean {
  return (
    response.category === category &&
    matchesAsinSet(response.asins, submission.asins) &&
    matchesOrderedStrings(response.reviewUrls, submission.reviewUrls)
  );
}

export function parseBatchMessageProvenance(
  source: unknown,
  modelId: unknown
): BatchMessageProvenance | null {
  if (source === "llm" && typeof modelId === "string" && modelId.trim()) {
    return { source, modelId: modelId.trim() };
  }
  if (source === "deterministic_fallback" && modelId === null) {
    return { source, modelId: null };
  }
  return null;
}

export function primaryViolationType(review: BatchableQueueReview): string {
  return primaryActionableViolation(review)?.type ?? "uncategorized";
}

export function primaryActionableViolation(
  review: BatchableQueueReview
): QueueViolation | null {
  return actionableViolations(review)[0] ?? null;
}

export function actionableViolations(
  review: BatchableQueueReview
): QueueViolation[] {
  return review.classification.violations.filter((violation) => {
    const type = violation.type?.trim();
    return (
      ACTIONABLE_VIOLATION_TYPES.has(type) &&
      Number.isFinite(violation.confidence) &&
      violation.confidence >= (VIOLATION_THRESHOLDS[type] ?? Number.POSITIVE_INFINITY)
    );
  });
}

export function actionableQueueReviews<T extends BatchableQueueReview>(
  reviews: T[]
): T[] {
  return reviews.filter((review) => primaryActionableViolation(review) !== null);
}

export function managedFilingQueueReviews<
  T extends { takedownCapability: string },
>(reviews: T[]): T[] {
  return reviews.filter(
    (review) => review.takedownCapability === "brand_registry_ready"
  );
}

function reviewOwnerKey(review: BatchableQueueReview): string {
  return (
    review.customer?.id ??
    review.brand?.customerId ??
    `email:${review.auditRequest.email.trim().toLowerCase()}`
  );
}

export function canonicalReviewUrl(
  review: Pick<BatchableQueueReview, "amazonReviewId" | "reviewUrl">
): string | null {
  const reviewId = review.amazonReviewId.trim();
  if (
    reviewId &&
    !/^(manual|seed)-/i.test(reviewId) &&
    /^[a-z0-9-]+$/i.test(reviewId)
  ) {
    return `https://www.amazon.com/gp/customer-reviews/${reviewId}/`;
  }

  if (!review.reviewUrl?.trim()) return null;
  try {
    const url = new URL(review.reviewUrl.trim());
    if (
      url.protocol !== "https:" ||
      (url.hostname !== "amazon.com" && !url.hostname.endsWith(".amazon.com"))
    ) {
      return null;
    }
    const match = url.pathname.match(/\/gp\/customer-reviews\/([^/]+)/i);
    const id = match?.[1];
    return id && !/^(manual|seed)-/i.test(id) && /^[a-z0-9-]+$/i.test(id)
      ? `https://www.amazon.com/gp/customer-reviews/${id}/`
      : null;
  } catch {
    return null;
  }
}

export function groupQueueReviews<T extends BatchableQueueReview>(
  reviews: T[]
): TakedownQueueGroup<T>[] {
  const grouped = new Map<
    string,
    { ownerKey: string; category: string; reviews: T[] }
  >();

  for (const review of actionableQueueReviews(reviews)) {
    const ownerKey = reviewOwnerKey(review);
    const category = primaryViolationType(review);
    const key = JSON.stringify([ownerKey, category]);
    const existing = grouped.get(key);
    if (existing) {
      existing.reviews.push(review);
    } else {
      grouped.set(key, { ownerKey, category, reviews: [review] });
    }
  }

  return Array.from(grouped.values())
    .sort(
      (left, right) =>
        TEXT_COLLATOR.compare(
          violationLabel(left.category),
          violationLabel(right.category)
        ) || TEXT_COLLATOR.compare(left.ownerKey, right.ownerKey)
    )
    .flatMap((group) => {
      const sorted = [...group.reviews].sort(
        (left, right) =>
          TEXT_COLLATOR.compare(left.asin, right.asin) ||
          TEXT_COLLATOR.compare(
            left.amazonReviewId || left.id,
            right.amazonReviewId || right.id
          )
      );
      const batchCount = Math.ceil(sorted.length / MAX_BRAND_REGISTRY_REVIEWS);
      return Array.from({ length: batchCount }, (_, index) => ({
        key: JSON.stringify([group.ownerKey, group.category, index]),
        ownerKey: group.ownerKey,
        category: group.category,
        batchNumber: index + 1,
        batchCount,
        reviews: sorted.slice(
          index * MAX_BRAND_REGISTRY_REVIEWS,
          (index + 1) * MAX_BRAND_REGISTRY_REVIEWS
        ),
      }));
    });
}

export function buildBrandRegistrySubmission<
  T extends BatchableQueueReview,
>(
  group: TakedownQueueGroup<T>,
  selectedReviewIds: Iterable<string>
):
  | { ok: true; fields: BrandRegistrySubmission; reviews: T[] }
  | { ok: false; error: string } {
  const requestedIds = Array.from(selectedReviewIds);
  if (requestedIds.length === 0) {
    return { ok: false, error: "Select at least one review." };
  }
  if (requestedIds.length > MAX_BRAND_REGISTRY_REVIEWS) {
    return {
      ok: false,
      error: `Amazon Brand Registry accepts at most ${MAX_BRAND_REGISTRY_REVIEWS} review URLs per submission.`,
    };
  }
  if (new Set(requestedIds).size !== requestedIds.length) {
    return { ok: false, error: "The selection contains duplicate reviews." };
  }

  const selected = new Set(requestedIds);
  const matched = group.reviews.filter((review) => selected.has(review.id));
  if (matched.length !== requestedIds.length) {
    return { ok: false, error: "One or more selected reviews are unavailable." };
  }
  if (
    !ACTIONABLE_VIOLATION_TYPES.has(group.category) ||
    matched.some(
      (review) => primaryViolationType(review) !== group.category
    )
  ) {
    return {
      ok: false,
      error:
        "Every selected review must have the same supported Amazon policy violation.",
    };
  }

  const asins = Array.from(
    new Set(matched.map((review) => review.asin.trim().toUpperCase()))
  ).sort(TEXT_COLLATOR.compare);
  if (asins.some((asin) => !/^[A-Z0-9]{10}$/.test(asin))) {
    return { ok: false, error: "One or more selected reviews has an invalid ASIN." };
  }
  if (asins.length > 10) {
    return {
      ok: false,
      error: "Amazon Brand Registry accepts at most 10 ASINs per submission.",
    };
  }

  const reviewUrls = matched.map(canonicalReviewUrl);
  if (reviewUrls.some((url) => !url)) {
    return {
      ok: false,
      error: "Every selected review needs a valid Amazon review URL.",
    };
  }
  const validReviewUrls = reviewUrls as string[];
  if (new Set(validReviewUrls).size !== validReviewUrls.length) {
    return {
      ok: false,
      error: "The selected reviews resolve to duplicate Amazon review URLs.",
    };
  }

  return {
    ok: true,
    reviews: matched,
    fields: {
      asinField: asins.join(", "),
      reviewUrlsField: validReviewUrls.join("\n"),
      asins,
      reviewUrls: validReviewUrls,
    },
  };
}
